/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Route } from './+types/_app.category.$categoryId';
/** @sfdc-extension-line SNK */
import type { ShopperProducts } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { getInitialFiltersOpen } from '@/hooks/use-filters-panel-state';
import { getLogger } from '@/lib/logger.server';
/** @sfdc-extension-block-start SNK */
import { CategoryPage, type CategoryPageData } from '@/pages/category';
import { fetchCategoryWithHierarchyFallback } from '@/extensions/snk/lib/hierarchy-url.server';
import {
    extractCategoryHierarchyPathFromRequest,
    redirectIfCategoryHierarchyIdentityMismatch,
    throwCanonicalRedirect,
} from '@/extensions/snk/lib/hierarchy-url-redirect.server';
import { routeHref, routes } from '@/route-paths';
import type { CategoryWithHierarchyUrl } from '@/extensions/snk/lib/hierarchy-url';
/** @sfdc-extension-block-end SNK */

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results',
        maxComponents: 5,
    },
    {
        id: 'plpTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'plpBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata {}

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<CategoryPageData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });

    /** @sfdc-extension-block-start SNK */
    const canonicalSlug = categoryId.toLowerCase();
    if (categoryId !== canonicalSlug) {
        throwCanonicalRedirect({
            context,
            request,
            targetPath: routeHref(routes.category, { categoryId: canonicalSlug }),
        });
    }
    /** @sfdc-extension-block-end SNK */

    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = config.search.products.hits.limit;

    // The client persists loaded count for back navigation. `?page=N` remains the crawler/no-JS path that
    // server-renders a single page window.
    const requestedOffset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10) || 0;
    const requestedPage = parseInt(searchParams.get('page') || '0', 10) || 0;
    const isPagedRequest = requestedPage > 1;
    const offset = isPagedRequest ? (requestedPage - 1) * limit : requestedOffset;
    const initialFetchLimit = limit;
    const safeCriticalCount = Math.min(config.search.products.hits.critical ?? 4, initialFetchLimit);

    let categoryData: ShopperProducts.schemas['Category'] | undefined;
    let effectiveCategoryId = categoryId;
    /** @sfdc-extension-block-start SNK */
    try {
        categoryData = await fetchCategoryWithHierarchyFallback(context, categoryId, 1);
        effectiveCategoryId = categoryData.id;

        redirectIfCategoryHierarchyIdentityMismatch({
            context,
            request,
            requestPath: extractCategoryHierarchyPathFromRequest(request),
            category: categoryData as CategoryWithHierarchyUrl,
        });
    } catch (e) {
        if (e instanceof Response) {
            throw e;
        }
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }
    /** @sfdc-extension-block-end SNK */

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const effectiveRefine = refine.filter((r) => !r.startsWith('cgid='));
    const selectedCgidRefine = refine.find((r) => r.startsWith('cgid='));
    effectiveRefine.push(selectedCgidRefine ?? `cgid=${effectiveCategoryId}`);

    const searchResultCritical = await fetchSearchProducts(context, {
        /** @sfdc-extension-line SNK */
        limit: safeCriticalCount,
        offset,
        sort,
        refine: effectiveRefine,
        currency,
    });

    // Intentionally await only the above-the-fold products. The remainder streams without delaying the initial
    // document; combining these requests would make the full configured page critical again.
    /** @sfdc-extension-block-start SNK */
    const criticalHitCount = searchResultCritical.hits?.length ?? 0;
    const remainingLimit = Math.max(0, initialFetchLimit - criticalHitCount);
    const searchResultNonCritical =
        remainingLimit > 0
            ? fetchSearchProducts(context, {
                  limit: remainingLimit,
                  offset: offset + criticalHitCount,
                  sort,
                  refine: effectiveRefine,
                  currency,
              })
            : Promise.resolve({
                  ...searchResultCritical,
                  hits: [],
                  offset: offset + criticalHitCount,
                  limit: 0,
              });
    /** @sfdc-extension-block-end SNK */
    const deferredProducts = searchResultNonCritical.then((result) => result.hits ?? []);

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // SEO / crawler pagination. Expose `rel=prev/next` URLs so bots can discover
    // and crawl the full result set via `?page=N` even though shoppers use the JS "load more" flow.
    // These use the clean path + a single `page` param (canonical stays the base URL; see SeoMeta).
    const totalCount = searchResultCritical.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / limit));
    const currentPage = isPagedRequest ? requestedPage : 1;
    const buildPageUrl = (p: number): string => {
        const params = new URLSearchParams();
        for (const r of refine) params.append('refine', r);
        if (sort) params.set('sort', sort);
        if (p > 1) params.set('page', String(p));
        const qs = params.toString();
        return `${requestUrl.origin}${requestUrl.pathname}${qs ? `?${qs}` : ''}`;
    };
    const seoPagination = {
        prevUrl: currentPage > 1 ? buildPageUrl(currentPage - 1) : null,
        nextUrl: currentPage < totalPages ? buildPageUrl(currentPage + 1) : null,
    };

    // Generate category schema in loader (server-side) for SEO
    /** @sfdc-extension-block-start SNK */
    const categorySchemaPromise = searchResultNonCritical
        .then((nonCritical) => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const schemaPageUrl = `${publicOrigin}${url.pathname}${url.search}`;
                // Validate inputs before generating schema
                if (!categoryData) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResultCritical,
                        hits: [...(searchResultCritical.hits ?? []), ...(nonCritical.hits ?? [])],
                    },
                    config,
                    pageUrl: schemaPageUrl,
                    defaultCurrency: currency,
                });
            } catch (error) {
                logger.error('Error generating category schema in loader', {
                    error,
                });
                return null;
            }
        })
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });
    /** @sfdc-extension-block-end SNK */

    return {
        category: categoryData,
        /** @sfdc-extension-line SNK */
        searchResultCritical,
        deferredProducts,
        page: fetchPageWithComponentData(args, {
            aspectType: 'plp',
            categoryId: effectiveCategoryId,
        }),
        categoryId: effectiveCategoryId,
        pageUrl,
        refine: effectiveRefine,
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        seoPagination,
        /** @sfdc-extension-line SNK */
        initialCount: Math.min(initialFetchLimit, Math.max(0, totalCount - offset)),
        offset,
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';
export default CategoryPage;
